/**
 * << MUI Custom Theme Configuration >>
 * 
 * This file defines the Material-UI (MUI) custom theme for the application. 
 * It centralizes and extends the default MUI theme to include:
 *
 * @Author: David Cannan @Cdaprod
 * @Version: 1.1.0
 * @Purpose: Provide a unified styling system with custom gradients, transitions, reusable mixins, 
 *           and seamless integration with core components like Navigation and Modal.
 *
 * -----------------------------------
 * @Customizations:
 * -----------------------------------
 * @Gradients:
 *   - `theme.custom.gradients.primary`: Linear gradient for primary colors.
 *   - `theme.custom.gradients.secondary`: Linear gradient for secondary colors.
 *   - `theme.custom.gradients.blur`: Blur effects for glassmorphism.
 *
 * @Transitions:
 *   - `theme.custom.transitions.quick`: 200ms for quick animations.
 *   - `theme.custom.transitions.medium`: 300ms for standard interactions.
 *   - `theme.custom.transitions.slow`: 500ms for longer transitions.
 *
 * @Mixins:
 *   - `theme.custom.mixins.glassmorphism`: Glassmorphism styling with blur effects for modern UI components.
 *   - `theme.custom.mixins.flexCenter`: Flexbox styles for centering content.
 *   - `theme.custom.mixins.absoluteCenter`: Absolute centering for components.
 *
 * @Typography:
 *   - Responsive font styles for headings, body, and buttons.
 *
 * @Component Overrides:
 *   - `MuiAppBar`: Adds glassmorphism effects for a modern and clean navigation bar.
 *   - `MuiButton`: Customizes hover and active effects for buttons.
 *   - `MuiDrawer`: Integrates gradients and blur effects for the navigation drawer.
 *   - `MuiModal`: Enhances modals with glassmorphism and backdrop effects.
 *
 * @Light/Dark Mode:
 *   - The theme supports light and dark modes with configurable options.
 *   - `theme('light')` or `theme('dark')` can be used to switch modes.
 *
 * @Responsive Design:
 *   - Custom breakpoints for responsive design across devices.
 *   - `theme.breakpoints.values` includes standard MUI values with flexibility for custom extensions.
 *
 * @Usage Guidelines:
 * 1. Use `theme.custom` for custom gradients, transitions, and mixins.
 * 2. Apply `theme.typography` for consistent font styling across components.
 * 3. Use `theme.palette` for colors and gradients, ensuring accessibility and contrast.
 *
 * @TypeScript Integration:
 * - This file includes type augmentation for `Theme` and ensures compatibility with TypeScript projects.
 * - Restart your dev server after making changes to see the updates.
 *
 * @Performance Notes:
 * - Overuse of glassmorphism effects may impact performance on low-end devices.
 * - Use sparingly in critical components like Navigation and Modals.
 *
 * @Contact:
 * - David Cannan @Cdaprod
 * - Cdaprod@Cdaprod.dev
 */

// ---------------------------
// TypeScript Theme Augmentation
// ---------------------------
// ---------------------------
// TypeScript Theme Augmentation
// ---------------------------
import '@mui/material/styles';
import {
  Shadows,
  PaletteColorOptions,
  createTheme,
  // alpha,
  responsiveFontSizes,
} from '@mui/material/styles';
import { Roboto } from 'next/font/google';

// Interfaces for Custom Theme Properties
interface CustomMixins {
  glassmorphism: React.CSSProperties;
  flexCenter: React.CSSProperties;
  absoluteCenter: React.CSSProperties;
}

interface CustomGradients {
  primary: string;
  secondary: string;
  blur: {
    light: string;
    medium: string;
    heavy: string;
  };
}

interface CustomTransitions {
  quick: string;
  medium: string;
  slow: string;
}

// Augmenting the MUI Theme
declare module '@mui/material/styles' {
  interface Palette {
    success: PaletteColor;
    error: PaletteColor;
    info: PaletteColor;
  }

  interface PaletteOptions {
    success?: PaletteColorOptions;
    error?: PaletteColorOptions;
    info?: PaletteColorOptions;
  }

  interface PaletteColor {
    gradient?: string;
  }

  interface Theme {
    custom: {
      gradients: CustomGradients;
      transitions: CustomTransitions;
      mixins: CustomMixins;
    };
  }

  interface ThemeOptions {
    custom?: {
      gradients?: Partial<CustomGradients>;
      transitions?: Partial<CustomTransitions>;
      mixins?: Partial<CustomMixins>;
    };
  }

  interface TypeBackground {
    glass: string;
  }
}

// ---------------------------
// Custom Shadows (Commented to Avoid Unused Error)
// ---------------------------
// If unused, comment out or integrate it properly later.
// const customShadows = {
//   elevation1: `0 4px 6px -1px ${alpha('#000000', 0.1)}, 0 2px 4px -1px ${alpha('#000000', 0.06)}`,
//   elevation2: `0 10px 15px -3px ${alpha('#000000', 0.1)}, 0 4px 6px -2px ${alpha('#000000', 0.05)}`,
//   elevation3: `0 20px 25px -5px ${alpha('#000000', 0.1)}, 0 10px 10px -5px ${alpha('#000000', 0.04)}`,
// };

// ---------------------------
// Configure Roboto Font
// ---------------------------
const roboto = Roboto({
  weight: ['300', '400', '500', '700'],
  subsets: ['latin'],
  display: 'swap',
});

// ---------------------------
// Theme Creation Function
// ---------------------------
const theme = (mode: 'light' | 'dark' = 'dark') => {
  const isLight = mode === 'light';

  const colors = {
    primary: {
      main: '#60a5fa',
      light: '#93c5fd',
      dark: '#2563eb',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#a855f7',
      light: '#c084fc',
      dark: '#7c3aed',
      contrastText: '#ffffff',
    },
    background: {
      default: isLight ? '#f8f8ff' : '#0a0a0a',
      glass: 'rgba(255, 255, 255, 0.05)', // For glassmorphism
    },
    text: {
      primary: isLight ? '#000000' : '#ffffff',
      secondary: isLight ? '#654ea3' : 'rgba(255, 255, 255, 0.7)',
      disabled: isLight ? 'rgba(0,0,0,0.5)' : 'rgba(255, 255, 255, 0.5)',
    },
    divider: 'rgba(255, 255, 255, 0.1)',
    error: {
      main: '#ef4444',
      gradient: 'linear-gradient(90deg, #f87171, #dc2626)',
    },
    success: {
      main: '#10b981',
      gradient: 'linear-gradient(90deg, #34d399, #059669)',
    },
  };

  const custom = {
    gradients: {
      primary: 'linear-gradient(90deg, #60a5fa, #a855f7)',
      secondary: 'linear-gradient(90deg, #a855f7, #ec4899)',
      blur: {
        light: 'blur(8px)',
        medium: 'blur(12px)',
        heavy: 'blur(16px)',
      },
    },
    transitions: {
      quick: 'all 0.2s ease-in-out',
      medium: 'all 0.3s ease-in-out',
      slow: 'all 0.5s ease-in-out',
    },
    mixins: {
      glassmorphism: {
        background: colors.background.glass,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        border: `1px solid ${colors.divider}`,
      },
      flexCenter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      absoluteCenter: {
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      },
    },
  };

  const typography = {
    fontFamily: roboto.style.fontFamily,
    h1: { fontSize: '2.5rem', fontWeight: 700 },
    h5: { fontSize: '1.5rem', fontWeight: 600 },
    body1: { fontSize: '1rem', lineHeight: 1.5 },
  };

  const breakpoints = {
    values: {
      xs: 0,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
    },
  };

  const components = {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.background.default,
          color: colors.text.primary,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          ...custom.mixins.glassmorphism,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          ...custom.mixins.glassmorphism,
        },
      },
    },
  };

  const baseTheme = createTheme({
    palette: colors,
    typography,
    breakpoints,
    components,
    custom,
    shadows: Array(25).fill('none') as Shadows, // Default shadows
  });

  return responsiveFontSizes(baseTheme);
};

// Export the theme
export default theme('dark');